
/* ============================== src/components/Modals/ChangePasswordModal.jsx ============================== */
// src/components/Modals/ChangePasswordModal.jsx
/**
 * Модальное окно смены пароля.
 *
 * Функционал:
 *   - Валидация надежности нового пароля (длина, регистр, цифры, спецсимволы).
 *   - Проверка совпадения нового пароля и его подтверждения.
 *   - Отправка запроса на сервер и обновление кэша пользователя (сброс баннера).
 *
 * Особенности UI:
 *   - Кнопка отправки находится в футере модалки (вне DOM-дерева формы).
 *   - Для поддержки отправки по клавише Enter используется нативный HTML5-механизм:
 *     кнопка получает type="submit" и атрибут form="change-password-form",
 *     связывающий её с формой по id.
 */
import { useState } from "react";
import { ModalShell } from "../ModalShell";
import { useToast } from "../../context/ToastContext";
import { api } from "../../api/client";
import { useQueryClient } from "@tanstack/react-query";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { usePasswordReveal } from "../../hooks/usePasswordReveal";
import { checkPasswordStrength } from "../../utils/passwordPolicy";

export const ChangePasswordModal = ({ onClose }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { shown: showPass, toggle: togglePassVisibility } = usePasswordReveal();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const { issues, isValid: isPasswordValid } =
    checkPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!oldPassword) {
      setError("Введите текущий пароль");
      return;
    }
    if (!isPasswordValid) {
      setError("Новый пароль не соответствует требованиям безопасности");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Пароли не совпадают");
      return;
    }

    setIsSaving(true);
    try {
      await api.post("/auth/change-password", { oldPassword, newPassword });
      showToast("Пароль успешно изменен", "success");

      await queryClient.invalidateQueries({ queryKey: ["me"] });
      onClose();
    } catch (err) {
      setError(err.message || "Не удалось сменить пароль");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      title="Смена пароля"
      onClose={onClose}
      width={420}
      showSave={false}
      footer={
        <div className="modal-foot">
          <div className="spacer" />
          <button
            type="button"
            className="btn ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            type="submit"
            form="change-password-form"
            className="btn primary"
            disabled={
              isSaving || !oldPassword || !newPassword || !confirmPassword
            }
          >
            {isSaving ? "Сохранение…" : "Сменить пароль"}
          </button>
        </div>
      }
    >
      <form
        id="change-password-form"
        className="p-4 flex flex-col gap-3"
        onSubmit={handleSubmit}
      >
        <div className="login-sub mb-2">
          Установленный вам пароль нужно периодически менять в целях
          безопасности.
        </div>

        <div>
          <label className="lbl">Текущий пароль</label>
          <div className="relative">
            <input
              className="inp"
              type={showPass ? "text" : "password"}
              autoComplete="current-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={togglePassVisibility}
              className="pass-toggle-btn"
            >
              <Ic d={ICONS.eye} size={18} />
            </button>
          </div>
        </div>

        <div>
          <label className="lbl">Новый пароль</label>
          <input
            className="inp"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <div className="pass-checks">
            {issues.map((i) => (
              <span key={i.t} className={i.ok ? "ok" : ""}>
                ✓ {i.t}
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="lbl">Повторите новый пароль</label>
          <input
            className="inp"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        {error && <div className="login-err mt-2">{error}</div>}
      </form>
    </ModalShell>
  );
};
